import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, X, FileText, Users, FileEdit, Loader2 } from 'lucide-react';
import { api } from '../App';
import { getTranslation } from '../i18n/translations';

/**
 * GlobalSearch — kompakt, çift-mode arama:
 *   - Mobil (mode="mobile"): sadece mercek ikonu; tıklanınca üstte açılır input.
 *   - Desktop (mode="desktop"): daima görünür, şık bir input; odaklandığında dropdown.
 *
 * Debounce'lu backend araması + navigate ile hızlı geçiş.
 */
const GlobalSearch = ({ mode = 'desktop', lang = 'tr' }) => {
  const t = (key) => getTranslation(lang, key);
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const [results, setResults] = useState({ proposals: [], customers: [], documents: [] });
  const [loading, setLoading] = useState(false);
  const inputRef = useRef(null);
  const containerRef = useRef(null);

  // Sarı highlight — regex-safe escape + case-insensitive match
  const highlight = (text, needle) => {
    if (!text || !needle || needle.trim().length < 1) return text;
    try {
      const esc = needle.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
      const re = new RegExp(`(${esc})`, 'ig');
      const parts = String(text).split(re);
      return parts.map((p, i) =>
        re.test(p) ? (
          <mark key={i} className="bg-yellow-200 text-slate-900 rounded px-0.5">{p}</mark>
        ) : (
          <React.Fragment key={i}>{p}</React.Fragment>
        )
      );
    } catch {
      return text;
    }
  };

  // Debounced search
  useEffect(() => {
    if (q.trim().length < 2) {
      setResults({ proposals: [], customers: [], documents: [] });
      return;
    }
    setLoading(true);
    const timer = setTimeout(async () => {
      try {
        const res = await api.get(`/search?q=${encodeURIComponent(q.trim())}&limit=5`);
        setResults(res.data || { proposals: [], customers: [], documents: [] });
      } catch (e) {
        setResults({ proposals: [], customers: [], documents: [] });
      } finally {
        setLoading(false);
      }
    }, 250);
    return () => clearTimeout(timer);
  }, [q]);

  // Close dropdown on outside click
  useEffect(() => {
    const onClick = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        if (mode === 'mobile') setOpen(false);
        else setOpen(false);
      }
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [mode]);

  // Escape closes
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') { setOpen(false); setQ(''); }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  // Focus input on open (mobile)
  useEffect(() => {
    if (open && inputRef.current) {
      setTimeout(() => inputRef.current?.focus(), 60);
    }
  }, [open]);

  const totalHits = results.proposals.length + results.customers.length + results.documents.length;
  const go = (path) => {
    navigate(path);
    setOpen(false);
    setQ('');
  };

  // Mobile: icon-only button, tap → expand
  if (mode === 'mobile') {
    return (
      <div ref={containerRef} className="relative">
        <button
          onClick={() => setOpen((v) => !v)}
          className="p-2 hover:bg-white/10 rounded-lg transition"
          data-testid="global-search-toggle-mobile"
          aria-label={t('searchPlaceholder')}
        >
          {open ? <X className="w-5 h-5" /> : <Search className="w-5 h-5" />}
        </button>
        {open && (
          <div className="fixed left-0 right-0 top-14 bg-slate-900/95 backdrop-blur-md border-b border-white/10 shadow-2xl px-3 py-3 z-40">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
              <input
                ref={inputRef}
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder={t('searchPlaceholder')}
                className="w-full ps-9 pe-9 py-2.5 rounded-lg bg-slate-800 border border-slate-700 text-white placeholder-slate-500 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400"
                data-testid="global-search-input-mobile"
              />
              {loading && <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 animate-spin" />}
            </div>
            <ResultsPanel results={results} loading={loading} q={q} totalHits={totalHits} onSelect={go} lang={lang} highlight={highlight} compact />
          </div>
        )}
      </div>
    );
  }

  // Desktop: inline elegant input
  return (
    <div ref={containerRef} className="relative w-full max-w-md">
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
        <input
          value={q}
          onChange={(e) => { setQ(e.target.value); setOpen(true); }}
          onFocus={() => setOpen(true)}
          placeholder={t('searchPlaceholder')}
          className="w-full ps-10 pe-10 py-2 rounded-full bg-white/10 border border-white/20 text-white placeholder-slate-300/70 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400 focus:bg-white/15 transition-all"
          data-testid="global-search-input-desktop"
        />
        {loading ? (
          <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-300 animate-spin" />
        ) : q ? (
          <button onClick={() => { setQ(''); setOpen(false); }} className="absolute right-2 top-1/2 -translate-y-1/2 p-1 hover:bg-white/10 rounded-full">
            <X className="w-3.5 h-3.5 text-slate-300" />
          </button>
        ) : null}
      </div>
      {open && q.trim().length >= 2 && (
        <div className="absolute top-full left-0 right-0 mt-2 bg-white rounded-xl shadow-2xl border border-slate-200 overflow-hidden z-50 max-h-[70vh] overflow-y-auto">
          <ResultsPanel results={results} loading={loading} q={q} totalHits={totalHits} onSelect={go} lang={lang} highlight={highlight} />
        </div>
      )}
    </div>
  );
};

// Sonuçları gruplu göster
const ResultsPanel = ({ results, loading, q, totalHits, onSelect, lang, highlight, compact }) => {
  const t = (key) => getTranslation(lang, key);
  const wrapCls = compact
    ? 'mt-3 bg-slate-800 rounded-lg border border-slate-700 max-h-[60vh] overflow-y-auto divide-y divide-slate-700'
    : 'divide-y divide-slate-100';
  const groupHeader = compact ? 'px-3 py-1.5 text-[10px] uppercase font-bold tracking-wider text-slate-400 bg-slate-900/50' : 'px-4 py-1.5 text-[10px] uppercase font-bold tracking-wider text-slate-500 bg-slate-50';
  const itemCls = compact ? 'w-full flex items-start gap-3 px-3 py-2.5 text-left hover:bg-slate-700/60 transition' : 'w-full flex items-start gap-3 px-4 py-3 text-left hover:bg-amber-50 transition';
  const titleCls = compact ? 'text-sm font-medium text-white truncate' : 'text-sm font-medium text-slate-900 truncate';
  const subCls = compact ? 'text-xs text-slate-400 truncate' : 'text-xs text-slate-500 truncate';

  if (loading && totalHits === 0) {
    return (
      <div className={compact ? 'mt-3 text-center text-slate-400 text-sm py-4' : 'px-4 py-6 text-center text-slate-400 text-sm'}>
        {t('searching')}
      </div>
    );
  }
  if (!loading && totalHits === 0 && q.trim().length >= 2) {
    return (
      <div className={compact ? 'mt-3 text-center text-slate-500 text-sm py-4' : 'px-4 py-6 text-center text-slate-500 text-sm'}>
        {t('noResults')}
      </div>
    );
  }

  return (
    <div className={wrapCls}>
      {results.proposals.length > 0 && (
        <div>
          <div className={groupHeader}>{t('navProposals')}</div>
          {results.proposals.map((p) => (
            <button key={p.id} onClick={() => onSelect(`/proposal/${p.id}`)} className={itemCls} data-testid={`search-result-proposal-${p.id}`}>
              <FileText className="w-4 h-4 mt-0.5 text-amber-500 flex-shrink-0" />
              <div className="min-w-0 flex-1">
                <div className={titleCls}>{highlight(p.title || t('untitledProposal'), q)}</div>
                <div className={subCls}>{highlight(p.customer_name || '—', q)}</div>
              </div>
            </button>
          ))}
        </div>
      )}
      {results.customers.length > 0 && (
        <div>
          <div className={groupHeader}>{t('navCustomers')}</div>
          {results.customers.map((c) => (
            <button key={c.id} onClick={() => onSelect(`/customers`)} className={itemCls} data-testid={`search-result-customer-${c.id}`}>
              <Users className="w-4 h-4 mt-0.5 text-teal-500 flex-shrink-0" />
              <div className="min-w-0 flex-1">
                <div className={titleCls}>{highlight(c.name, q)}</div>
                <div className={subCls}>{highlight(c.email || c.phone || c.company || '—', q)}</div>
              </div>
            </button>
          ))}
        </div>
      )}
      {results.documents.length > 0 && (
        <div>
          <div className={groupHeader}>{t('navTextDocs')}</div>
          {results.documents.map((d) => (
            <button key={d.id} onClick={() => onSelect(`/text-editor/${d.id}`)} className={itemCls} data-testid={`search-result-doc-${d.id}`}>
              <FileEdit className="w-4 h-4 mt-0.5 text-emerald-500 flex-shrink-0" />
              <div className="min-w-0 flex-1">
                <div className={titleCls}>{highlight(d.title || t('untitledProposal'), q)}</div>
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default GlobalSearch;
